/**
 * QL-287 — Cash Transfers body: the instance's planned transfers with
 * Edit / Delete per row and "Add transfer" below the list. Owns the add / edit
 * modal and the delete confirm; the advisor entry injects data + mutations.
 */

import type {
  CashAccount,
  CashTransfer,
  CashTransfersResponse,
  CreateCashTransferRequest,
} from "@repo/shared/types";
import { Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { VisualizationContainer } from "@/components/flow/visualization-container";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableActionsCell,
  TableActionsHead,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useConfirm } from "@/hooks/use-confirm";
import { useT } from "@/lib/i18n/use-t";
import type { CashTransfersConfig } from "@/lib/types/flow";
import { CashTransferForm } from "./cash-transfer-form";
import { T } from "./cash-transfers-view.i18n";
import { type FormatNumberFn, formatWhole } from "./types";

type MutationResult = { success: boolean; error?: string };

type CashTransfersViewProps = {
  instanceId: string;
  data: CashTransfersResponse;
  config: CashTransfersConfig;
  /** Locked instance — no add / edit / delete controls. */
  disabled: boolean;
  isPending: boolean;
  fmt: FormatNumberFn;
  onCreate: (data: CreateCashTransferRequest) => Promise<MutationResult>;
  onUpdate: (transferId: string, data: CreateCashTransferRequest) => Promise<MutationResult>;
  onDelete: (transferId: string) => Promise<MutationResult>;
};

function AccountName({ account }: { account: CashAccount | undefined }) {
  const t = useT(T);
  if (!account) return <span className="text-muted-foreground">{t.unknownAccount}</span>;
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {account.name}
      {account.isNew && <Badge variant="secondary">{t.newAccount}</Badge>}
    </span>
  );
}

export function CashTransfersView({
  instanceId,
  data,
  config,
  disabled,
  isPending,
  fmt,
  onCreate,
  onUpdate,
  onDelete,
}: CashTransfersViewProps) {
  const t = useT(T);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<CashTransfer | null>(null);
  const { confirm, ConfirmationDialog } = useConfirm();
  const accountByKey = new Map(data.accounts.map((a) => [a.key, a]));
  const nameOf = (key: string) => accountByKey.get(key)?.name ?? t.unknownAccount;

  const handleDelete = async (transfer: CashTransfer) => {
    const confirmed = await confirm({
      title: t.deleteTransfer,
      description: t.deleteConfirmation({
        amount: formatWhole(transfer.amount, fmt),
        currency: transfer.currency,
        from: nameOf(transfer.fromAccountKey),
        to: nameOf(transfer.toAccountKey),
      }),
      confirmText: t.delete,
      cancelText: t.cancel,
      variant: "destructive",
    });
    if (!confirmed) return;
    const result = await onDelete(transfer.id);
    if (!result.success) toast.error(t.error, { description: result.error || t.failedToDelete });
  };

  return (
    <>
      <VisualizationContainer config={config}>
        {data.transfers.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t.noTransfers}</p>
        ) : (
          <Table variant="data">
            <TableHeader>
              <TableRow>
                <TableHead>{t.fromAccount}</TableHead>
                <TableHead>{t.toAccount}</TableHead>
                <TableHead className="text-right">{t.amount}</TableHead>
                <TableHead>{t.currency}</TableHead>
                {!disabled && <TableActionsHead>{t.actions}</TableActionsHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.transfers.map((transfer) => (
                <TableRow key={transfer.id} data-testid="cash-transfer-row">
                  <TableCell>
                    <AccountName account={accountByKey.get(transfer.fromAccountKey)} />
                  </TableCell>
                  <TableCell>
                    <AccountName account={accountByKey.get(transfer.toAccountKey)} />
                  </TableCell>
                  <TableCell className="text-right tabular-nums whitespace-nowrap">
                    {formatWhole(transfer.amount, fmt)}
                  </TableCell>
                  <TableCell>{transfer.currency}</TableCell>
                  {!disabled && (
                    <TableActionsCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        tooltip={t.edit}
                        disabled={isPending}
                        onClick={() => setEditing(transfer)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        tooltip={t.delete}
                        disabled={isPending}
                        onClick={() => handleDelete(transfer)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableActionsCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {!disabled && (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            // A transfer needs two accounts.
            disabled={isPending || data.accounts.length < 2}
            onClick={() => setAdding(true)}
          >
            {t.addTransfer}
          </Button>
        )}
      </VisualizationContainer>

      {(adding || editing) && (
        <CashTransferForm
          key={editing?.id ?? "new"}
          instanceId={instanceId}
          transfer={editing}
          accounts={data.accounts}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onSave={(input) => (editing ? onUpdate(editing.id, input) : onCreate(input))}
        />
      )}

      <ConfirmationDialog />
    </>
  );
}
